// Modal overlays, dialogs and toasts.
import { button, clear, h } from './dom.js';

export interface ModalHandle {
  body: HTMLElement;
  close(): void;
}

let modalHost: HTMLElement | null = null;

function host(): HTMLElement {
  if (!modalHost) {
    modalHost = h('div', { class: 'modal-host', id: 'modal-host' });
    document.body.append(modalHost);
  }
  return modalHost;
}

/** Opens a modal dialog; returns its body element for the caller to fill. */
export function openModal(opts: { title: string; wide?: boolean; kind?: string; onClose?: () => void }): ModalHandle {
  const body = h('div', { class: 'modal-body' });
  const overlay = h('div', { class: 'modal-overlay' });
  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
    opts.onClose?.();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };
  const dialog = h('div', { class: `modal${opts.wide ? ' modal-wide' : ''}${opts.kind ? ` modal-${opts.kind}` : ''}` }, [
    h('div', { class: 'modal-head' }, [
      h('h2', { text: opts.title }),
      button('✕', 'icon-button', close, 'Close'),
    ]),
    body,
  ]);
  overlay.append(dialog);
  overlay.addEventListener('mousedown', (event) => {
    if (event.target === overlay) close();
  });
  if (opts.kind) {
    // A singleton panel (quick switcher, git, settings) replaces its older instance.
    for (const stale of document.querySelectorAll(`.modal-overlay:has(.modal-${opts.kind})`)) stale.remove();
  }
  host().append(overlay);
  document.addEventListener('keydown', onKey);
  return { body, close };
}

export function confirmDialog(title: string, message: string, confirmLabel = 'Confirm'): Promise<boolean> {
  const { promise, resolve } = Promise.withResolvers<boolean>();
  let settled = false;
  const finish = (value: boolean): void => {
    if (settled) return;
    settled = true;
    resolve(value);
  };
  const modal = openModal({ title, onClose: () => finish(false) });
  modal.body.append(
    h('p', { class: 'modal-text', text: message }),
    h('div', { class: 'modal-actions' }, [
      button('Cancel', 'ghost-button', () => modal.close()),
      button(confirmLabel, 'primary-button danger', () => {
        finish(true);
        modal.close();
      }),
    ]),
  );
  return promise;
}

export function promptDialog(opts: {
  title: string;
  label: string;
  value?: string;
  placeholder?: string;
  confirmLabel?: string;
}): Promise<string | null> {
  const { promise, resolve } = Promise.withResolvers<string | null>();
  let settled = false;
  const finish = (value: string | null): void => {
    if (settled) return;
    settled = true;
    resolve(value);
  };
  const modal = openModal({ title: opts.title, onClose: () => finish(null) });
  const input = h('input', {
    class: 'text-input',
    type: 'text',
    value: opts.value ?? '',
    ...(opts.placeholder ? { placeholder: opts.placeholder } : {}),
  });
  const submit = (): void => {
    finish(input.value.trim());
    modal.close();
  };
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') submit();
  });
  modal.body.append(
    h('label', { class: 'field' }, [h('span', { text: opts.label }), input]),
    h('div', { class: 'modal-actions' }, [
      button('Cancel', 'ghost-button', () => modal.close()),
      button(opts.confirmLabel ?? 'OK', 'primary-button', submit),
    ]),
  );
  input.focus();
  input.select();
  return promise;
}

export interface ToastOptions {
  kind?: 'info' | 'error' | 'success';
  actionLabel?: string;
  onAction?: () => void;
  timeoutMs?: number;
}

let toastHost: HTMLElement | null = null;

/** Shows a transient toast in the bottom-right corner; optional single action. */
export function toast(message: string, options: ToastOptions = {}): void {
  if (!toastHost) {
    toastHost = h('div', { class: 'toast-host' });
    document.body.append(toastHost);
  }
  const node = h('div', { class: `toast toast-${options.kind ?? 'info'}` }, [
    h('span', { class: 'toast-text', text: message }),
  ]);
  if (options.actionLabel && options.onAction) {
    const action = options.onAction;
    node.append(
      button(options.actionLabel, 'toast-action', () => {
        node.remove();
        action();
      }),
    );
  }
  toastHost.append(node);
  const timeout = options.timeoutMs ?? (options.actionLabel ? 12000 : 4000);
  window.setTimeout(() => node.remove(), timeout);
}

/** Renders a small key/value grid used by history, git and settings panels. */
export function definitionList(entries: [string, ChildNode | string][]): HTMLElement {
  const dl = h('dl', { class: 'kv' });
  for (const [key, value] of entries) {
    dl.append(h('dt', { text: key }), h('dd', {}, [typeof value === 'string' ? document.createTextNode(value) : value]));
  }
  return dl;
}

export function section(title: string, children: (Node | string)[]): HTMLElement {
  return h('section', { class: 'panel-section' }, [h('h3', { text: title }), ...children]);
}

export function emptyNote(text: string): HTMLElement {
  return h('p', { class: 'empty-note', text });
}

export function rerender(target: HTMLElement, nodes: (Node | string)[]): void {
  clear(target);
  for (const node of nodes) target.append(node);
}
